import { Link } from '@inertiajs/react'
import { Calendar } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { FeedActor, FeedUserRsvpItem } from '@/types'
import { actorInitials, actorName, formatEventDate, relativeTime } from './actor_helpers'

type Props = { item: FeedUserRsvpItem }

function ActorAvatar({ actor }: { actor: FeedActor }) {
  return (
    <Avatar className="h-10 w-10">
      {actor.avatar_url && <AvatarImage src={actor.avatar_url} alt={actor.username} />}
      <AvatarFallback>{actorInitials(actor)}</AvatarFallback>
    </Avatar>
  )
}

export default function UserRsvpFeedItem({ item }: Props) {
  const { actor, event, created_at } = item
  const profileHref = actor.profile_visible ? `/u/${actor.username}` : null

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-black/5 bg-card p-4 shadow-xs">
      {profileHref ? (
        <Link href={profileHref} className="shrink-0">
          <ActorAvatar actor={actor} />
        </Link>
      ) : (
        <div className="shrink-0">
          <ActorAvatar actor={actor} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-base">
          {profileHref ? (
            <Link href={profileHref} className="font-semibold text-foreground hover:underline">
              {actorName(actor)}
            </Link>
          ) : (
            <span className="font-semibold text-foreground">{actorName(actor)}</span>
          )}
          <span className="text-muted-foreground"> RSVP&apos;d for </span>
          <Link href={`/events/${event.slug}`} className="font-semibold text-foreground hover:underline">
            {event.title}
          </Link>
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {event.date && <>{formatEventDate(event.date)} · </>}
          {relativeTime(created_at)}
        </p>
      </div>

      <Link href={`/events/${event.slug}`} className="shrink-0">
        {event.cover_image_url ? (
          <img
            src={event.cover_image_url}
            alt=""
            loading="lazy"
            className="h-14 w-14 rounded-lg object-cover ring-1 ring-black/5"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/8 ring-1 ring-black/5">
            <Calendar className="size-5 text-primary/40" />
          </div>
        )}
      </Link>
    </div>
  )
}
